import { RFX_SUBMISSION_STATUS } from "../constants";

/**
 * Single source of truth for "what colour is this INVITE/SUBMISSION status".
 *
 * This is DELIBERATELY separate from `features/Rfx/utils/rfx-status-colour.ts`,
 * which colours the RFx's own lifecycle (Active / On Hold / Cancelled /
 * Archived …). The two are different domains with different members and
 * different palettes — this set has `UNSUCCESSFUL`, which has no meaning as an
 * RFx status, and the RFx set has `EXPIRED`/`SUSPENDED`, which a bidder's
 * response can never be. Colouring one from the other's table silently maps
 * unknown members onto the neutral default, so they stay apart on purpose.
 *
 * Extracted verbatim from the `responseStatuses` array that used to be private
 * to `RfxSubmissionStatusPill`, which now consumes this. The bidder card's top
 * stripe and the pill sitting inside that card have to agree, and the only way
 * to guarantee that is for both to read the same table.
 *
 * `color` is the text/fill shade; `colorBorder` is the stronger outline shade.
 * Both are raw hex rather than Tailwind tokens because the pill applies them as
 * inline styles, and these exact values are what shipped.
 */
export interface RfxSubmissionStatusPresentation {
	color: string;
	colorBorder: string;
	displayText: string;
	type: string;
}

export const RFX_SUBMISSION_STATUS_PRESENTATION: RfxSubmissionStatusPresentation[] =
	[
		{
			displayText: "Submitted",
			color: "#00976B",
			colorBorder: "#00A87C",
			type: RFX_SUBMISSION_STATUS.SUBMITTED,
		},
		{
			displayText: "In Progress",
			color: "#0031DD",
			colorBorder: "#003CE9",
			type: RFX_SUBMISSION_STATUS.INPROGRESS,
		},
		{
			displayText: "Declined",
			color: "#B42318",
			colorBorder: "#D92D20",
			type: RFX_SUBMISSION_STATUS.DECLINE,
		},
		{
			displayText: "Awarded",
			color: "#00976B",
			colorBorder: "#00A87C",
			type: RFX_SUBMISSION_STATUS.AWARDED,
		},
		{
			displayText: "Invited",
			color: "#0031DD",
			colorBorder: "#003CE9",
			type: RFX_SUBMISSION_STATUS.INVITED,
		},
		{
			displayText: "Accepted",
			color: "#00976B",
			colorBorder: "#00A87C",
			type: RFX_SUBMISSION_STATUS.ACCEPTED,
		},
		{
			displayText: "Unsuccessful",
			color: "#B42318",
			colorBorder: "#D92D20",
			type: RFX_SUBMISSION_STATUS.UNSUCCESSFUL,
		},
		{
			displayText: "Being Revised",
			color: "#0031DD",
			colorBorder: "#003CE9",
			type: RFX_SUBMISSION_STATUS.BEING_REVISED,
		},
	];

export const getRfxSubmissionStatusPresentation = (
	status: string | null | undefined
): RfxSubmissionStatusPresentation | undefined =>
	RFX_SUBMISSION_STATUS_PRESENTATION.find(
		(entry) => entry.type === status?.toUpperCase()
	);

/**
 * Stripe/outline colour for a bidder card top border.
 *
 * Returns the `colorBorder` shade so the stripe matches the pill's outline
 * rather than its lighter text fill. `null` for an unknown or absent status —
 * callers decide the fallback, because silently defaulting to a real status
 * colour would misrepresent the row.
 */
export const getRfxSubmissionStatusStripeColour = (
	status: string | null | undefined
): string | null => getRfxSubmissionStatusPresentation(status)?.colorBorder ?? null;
